import { useMediaQuery } from 'react-responsive'
import {
   DESKTOP_MIN_WIDTH,
   TABLET_MIN_WIDTH
} from '../../constants/ResponsiveConstants'

export const geDeviceWidth = (): number => window.innerWidth

export function isMobile(): boolean {
   const width = geDeviceWidth()
   return width < TABLET_MIN_WIDTH
}

export function isTablet(): boolean {
   const width = geDeviceWidth()
   return width >= TABLET_MIN_WIDTH && width < DESKTOP_MIN_WIDTH
}

export function isDesktop(): boolean {
   const width = geDeviceWidth()
   return width >= DESKTOP_MIN_WIDTH
}

export function isTabletOrDesktop(): boolean {
   return isTablet() || isDesktop()
}

export function isTabletOrMobile(): boolean {
   return isTablet() || isMobile()
}

export const Desktop = ({ children }): JSX.Element => {
   const isDesktop = useMediaQuery({ minWidth: DESKTOP_MIN_WIDTH })
   return isDesktop ? children : null
}

export const Tablet = ({ children }): JSX.Element => {
   const isTablet = useMediaQuery({
      minWidth: TABLET_MIN_WIDTH,
      maxWidth: DESKTOP_MIN_WIDTH - 1
   })
   return isTablet ? children : null
}

export const Mobile = ({ children }): JSX.Element => {
   const isMobile = useMediaQuery({ maxWidth: TABLET_MIN_WIDTH - 1 })
   return isMobile ? children : null
}

export const MobileOrTablet = ({ children }): JSX.Element => {
   const isMobileOrTablet = useMediaQuery({ maxWidth: DESKTOP_MIN_WIDTH - 1 })
   return isMobileOrTablet ? children : null
}

export const TabletOrDesktop = ({ children }): JSX.Element => {
   const isTabletOrDesktop = useMediaQuery({ minWidth: TABLET_MIN_WIDTH })
   return isTabletOrDesktop ? children : null
}

export const LandingPage = ({ mobile, desktop, children }): JSX.Element => {
   const isMobile = useMediaQuery({ maxWidth: TABLET_MIN_WIDTH - 1 })
   return children(isMobile ? mobile : desktop)
}

export const CustomDeviceWidth = ({
   minWidth,
   maxWidth,
   children
}): JSX.Element => {
   const isDeviceInSpecifiedWidth = useMediaQuery({
      maxWidth,
      minWidth
   })
   return isDeviceInSpecifiedWidth ? children : null
}

export const CustomBreakPoint = ({
   breakPoint,
   belowBreakPoint,
   aboveBreakPoint,
   children
}): JSX.Element => {
   const isBelowBreakPoint = useMediaQuery({
      maxWidth: breakPoint
   })

   return children(isBelowBreakPoint ? belowBreakPoint : aboveBreakPoint)
}

export const getStylePropertyValue = (
   element: HTMLElement,
   styleProperty: string
): number =>
   parseInt(
      window.getComputedStyle(element, null).getPropertyValue(styleProperty) ??
         0,
      10
   )

export const getChildWidth = (children: any): number => {
   const items: any[] = Array.from(children)
   const child = items[0]
   let width = child.offsetWidth
   const styleProperties: string[] = [
      'padding-left',
      'padding-right',
      'margin-left',
      'margin-right',
      'border-left-width',
      'border-right-width'
   ]
   styleProperties.forEach(propertyName => {
      width += getStylePropertyValue(child, propertyName)
   })
   return width
}

export const calculateNumberOfItemsForRow = (children: any): number => {
   const items: any[] = Array.from(children)
   const baseOffset = items[0].offsetTop
   const breakIndex = items.findIndex(item => item.offsetTop > baseOffset)
   return breakIndex === -1 ? items.length : breakIndex
}

export const calculateNumberOfRowsOfFlexBox = (children: any): number => {
   const items: any[] = Array.from(children)
   let baseOffset = items[0].offsetTop
   let numberOfRows = 0

   items.forEach(item => {
      if (item.offsetTop > baseOffset) {
         baseOffset = item.offsetTop
         numberOfRows += 1
      }
   })
   return numberOfRows
}

export function isTouchDevice(): boolean {
   return 'ontouchstart' in window || navigator.maxTouchPoints > 0
}
